import { Injectable, effect, inject, untracked } from '@angular/core';
import { DatiqLogService } from '@dat/datiq-log';
import {
    EventInfo,
    EventQuery,
    IqEventBusBrokerService,
    IqEventBusNode,
    IqEventBusQueueService,
    createCacheEventName,
    matchQuery,
} from '@dat/iq-event-bus';
import { isEmpty, pick } from 'lodash';
import { addRxPlugin, createRxDatabase } from 'rxdb';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { eventCacheSchema } from './iq-builder-event.cache.schemas';

addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);

@Injectable({
    providedIn: 'root',
})
export class IdBuilderEventCacheService {
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();
    private _db;
    private _collection;
    private _initialized: boolean = false;
    private _log: DatiqLogService = inject(DatiqLogService);
    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    constructor() {
        effect(() => {
            const eventNodes: IqEventBusNode[] = this._queue.eventNodes();
            eventNodes
                .filter((node) => !node.evt.handled)
                .forEach((node) => {
                    untracked(async () => {
                        this._queue.handleEvent(node);

                        try {
                            const test = await this._collection.events.upsert({
                                ...pick(node, ['id', 'topic', 'evt']),
                            });
                        } catch (error) {
                            console.log('Error caching event', error);
                            this._log.error('Error caching event', error);
                        }
                    });
                });
        });
    }

    async initialize(): Promise<void> {
        if (this._initialized) return;
        this._initialized = true;

        try {
            this._db = await createRxDatabase({
                name: 'event-cache',
                storage: getRxStorageDexie(),
            });
            this._collection = await this._db.addCollections({
                events: {
                    schema: eventCacheSchema,
                    migrationStrategies: {},
                },
            });
        } catch (error) {
            console.log('Error initializing event cache', error);
            this._log.error('Error initializing event cache', error);
        }
    }

    async fireEvents(id: string): Promise<void> {
        if (!this._initialized) return;

        const events = await this._collection?.events
            .find({
                selector: {
                    id: {
                        $regex: `^${id}:`,
                    },
                },
            })
            .exec();

        events?.forEach((event) => {
            this._eventBus.dispatch({
                name: event.evt.name,
                payload: event.evt.payload,
            });
        });
    }

    cache(dashboardId: string, event: EventInfo, query: EventQuery[]) {
        if (!query?.length) return;

        const shouldCache = query
            .filter((q) => !isEmpty(q))
            .some((q) => matchQuery(event, q));

        if (!shouldCache) return;

        this._queue.addCacheEvent({
            cacheId: dashboardId ?? null,
            evt: {
                ...event,
                name: createCacheEventName(event.name),
            },
        });
    }
}
